import React, { useState, useEffect } from 'react';
import { Download, Loader, Check } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * HistorialAcademicoEstudiante - Historial Académico para estudiantes matriculados
 * Código: A.E.-XXX-YY (Admisión Especial)
 * Dos vistas: Periodos 1-4 y Periodo Final
 */
const HistorialAcademicoEstudiante = () => {
    const [loading, setLoading] = useState(true);
    const [estudiante, setEstudiante] = useState({});
    const [notas, setNotas] = useState([]);
    const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
    const [periodoSeleccionado, setPeriodoSeleccionado] = useState('primer');
    const [aniosDisponibles, setAniosDisponibles] = useState([new Date().getFullYear()]);
    const [historialType, setHistorialType] = useState('licenciatura');
    const [notasPre, setNotasPre] = useState([]);

    const periodos = [
        { value: 'primer', label: 'Primer Periodo' },
        { value: 'segundo', label: 'Segundo Periodo' },
        { value: 'tercer', label: 'Tercer Periodo' },
        { value: 'cuarto', label: 'Cuarto Periodo' },
        { value: 'final', label: 'Periodo Final' }
    ];


    const sortNotasCorrelativo = (arr) => {
        if (!Array.isArray(arr)) return [];
        return [...arr].sort((a, b) => {
            const nivelA = parseInt(a.nivel) || 0;
            const nivelB = parseInt(b.nivel) || 0;
            if (nivelA !== nivelB) return nivelA - nivelB;
            const codeA = a.sigla || a.codigo || a.codigo_asignatura || '';
            const codeB = b.sigla || b.codigo || b.codigo_asignatura || '';
            const numA = parseInt(codeA.replace(/\D/g, ''), 10) || 0;
            const numB = parseInt(codeB.replace(/\D/g, ''), 10) || 0;
            if (numA !== numB) return numA - numB;
            return codeA.localeCompare(codeB);
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const queryParams = new URLSearchParams(window.location.search);
                const id_usuario = queryParams.get('id_usuario') || user?.id_usuario || '';
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                // 1. Obtener info del estudiante (Siempre necesario para saber la modalidad)
                const infoRes = await fetch(`${API_URL}/estudiante/info-matriculacion?id_usuario=${id_usuario}`, { headers });
                let estudianteData = {};
                if (infoRes.ok) {
                    const infoData = await infoRes.json();
                    estudianteData = infoData.estudiante || {};
                    setEstudiante(estudianteData);
                }

                if (historialType === 'licenciatura') {
                    // Lógica original de Licenciatura - SOLO notas de licenciatura
                    const notasRes = await fetch(
                        `${API_URL}/estudiante/notas-periodo?anio=${anioSeleccionado}&periodo=${periodoSeleccionado}&id_usuario=${id_usuario}`,
                        { headers }
                    );

                    let notasActuales = [];
                    if (notasRes.ok) {
                        const notasData = await notasRes.json();
                        notasActuales = notasData.notas || [];
                        if (notasData.anios) {
                            const currentYear = new Date().getFullYear();
                            const uniqueYears = Array.from(new Set([...notasData.anios, currentYear]));
                            setAniosDisponibles(uniqueYears.sort((a, b) => b - a));
                        }
                    }

                    // Solo cargar horarios de licenciatura si el estudiante está matriculado (tiene carrera asignada)
                    const estaMatriculado = estudianteData.carrera || estudianteData.id_carrera;
                    if (estaMatriculado) {
                        try {
                            const horarioRes = await fetch(
                                `${API_URL}/estudiante/horarios/disponibles/${anioSeleccionado}?id_usuario=${id_usuario}`,
                                { headers }
                            );
                            if (horarioRes.ok) {
                                const horarioData = await horarioRes.json();
                                const inscritasIds = horarioData.horarioSeleccionado || [];
                                const todosLosHorarios = horarioData.horarios || [];
                                const misInscripciones = todosLosHorarios.filter(h => {
                                    const hId = String(h.id);
                                    return inscritasIds.some(item => String(typeof item === 'object' ? item.id : item) === hId);
                                });

                                const materiasInscritas = [];
                                misInscripciones.forEach(ins => {
                                    const exists = materiasInscritas.find(m => (ins.sigla && m.sigla === ins.sigla) || (ins.asignatura && m.asignatura === ins.asignatura));
                                    if (!exists) materiasInscritas.push(ins);
                                });

                                const notasCombinadas = [...notasActuales];
                                materiasInscritas.forEach(ins => {
                                    const yaTieneNota = notasCombinadas.some(n =>
                                        (ins.sigla && n.sigla?.toLowerCase().trim() === ins.sigla?.toLowerCase().trim()) ||
                                        (ins.asignatura && n.asignatura?.toLowerCase().trim() === ins.asignatura?.toLowerCase().trim())
                                    );
                                    if (!yaTieneNota) {
                                        notasCombinadas.push({
                                            id_nota: `temp-${ins.id}`,
                                            sigla: ins.sigla, asignatura: ins.asignatura, paralelo: ins.paralelo,
                                            nivel: ins.nivel || estudianteData.nivel || '',
                                            promedio_proceso: null, practica: null, teoria: null, promedio_eval_final: null, nota_final: null,
                                            p1: null, p2: null, p3: null, p4: null, es_homologada: ins.es_homologada, docente: ins.docente || 'Por asignar'
                                        });
                                    }
                                });
                                setNotas(sortNotasCorrelativo(notasCombinadas));
                            } else {
                                setNotas(sortNotasCorrelativo(notasActuales));
                            }
                        } catch (err) {
                            setNotas(sortNotasCorrelativo(notasActuales));
                        }
                    } else {
                        // Sin carrera asignada: solo mostrar notas que ya existen, sin agregar materias de horarios
                        setNotas(sortNotasCorrelativo(notasActuales));
                    }
                } else {
                    // Lógica para CPA o PAA
                    const endpoint = historialType === 'cpa' ? 'notas-preadmision' : 'notas-prueba-admision';
                    const respNotas = await fetch(`${API_URL}/estudiante/${endpoint}?id_usuario=${id_usuario}`, { headers });
                    if (respNotas.ok) {
                        const dataNotas = await respNotas.json();
                        setNotasPre(sortNotasCorrelativo(dataNotas.notas || []));
                    }
                }

            } catch (error) {
                console.error('Error cargando historial:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [anioSeleccionado, periodoSeleccionado, historialType]);

    // Calcular estado según nota (Nota mínima de aprobación: 61)
    const calcularEstado = (notaFinal) => {
        if (notaFinal === null || notaFinal === undefined || notaFinal === 0) return 'NO SE PRESENTÓ';
        return parseFloat(notaFinal) >= 61 ? 'APROBADO' : 'REPROBADO';
    };

    // Formatea un valor numérico como entero (sin decimales). Devuelve '-' si no es válido.
    const formatEntero = (val) => {
        if (val === null || val === undefined || val === '') return '-';
        const n = parseFloat(val);
        if (isNaN(n) || n <= 0) return '-';
        return String(Math.round(n));
    };

    // Convierte la nota final a su equivalente en palabras (CERO, CINCUENTA, SESENTA, etc.)
    // en vez de letras A-F. Misma lógica que numeroALetras() del backend (Acta de Calificaciones).
    const calcularNotaLiteral = (nota) => {
        if (nota === null || nota === undefined || nota === '') return '';
        const n = Math.round(parseFloat(nota));
        if (isNaN(n) || n <= 0) return '';

        const unidades = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
        const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
        const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];

        if (n === 100) return 'CIEN';

        let letras = '';
        if (n >= 10 && n < 20) {
            letras = especiales[n - 10];
        } else if (n >= 20 && n < 30) {
            const u = n % 10;
            if (u === 0) letras = 'VEINTE';
            else if (u === 1) letras = 'VEINTIUNO';
            else letras = 'VEINTI' + unidades[u];
        } else if (n < 10) {
            letras = unidades[n];
        } else {
            const d = Math.floor(n / 10);
            const u = n % 10;
            letras = decenas[d];
            if (d > 0 && u > 0) letras += ' Y ' + unidades[u];
        }
        return letras;
    };

    // --- NUEVAS HELPERS PARA PROMEDIO FINAL ---

    // Calcula la nota final "efectiva" de una materia en el Periodo Final.
    // El backend no siempre llena nota_final (llega como 0/NULL antes del cierre),
    // así que si no hay una nota_final válida (>0), se calcula el promedio de
    // los periodos (1°P-4°P) que sí tengan nota registrada.
    const obtenerNotaFinalEfectiva = (nota) => {
        if (nota.nota_final !== null && nota.nota_final !== undefined && parseFloat(nota.nota_final) > 0) {
            return parseFloat(nota.nota_final);
        }
        const periodos = [nota.p1, nota.p2, nota.p3, nota.p4]
            .map(p => (p !== null && p !== undefined && p !== '' ? parseFloat(p) : null))
            .filter(p => p !== null && !isNaN(p) && p > 0);
        if (periodos.length === 0) return null;
        const suma = periodos.reduce((acc, v) => acc + v, 0);
        return Math.round(suma / periodos.length);
    };

    // Promedio de nota_final de licenciatura (solo materias con nota > 0)
    // getNota permite indicar de dónde sacar la nota de cada materia:
    //  - Periodos 1-4: usa nota.nota_final directamente (ya viene calculada por periodo)
    //  - Periodo Final: usa obtenerNotaFinalEfectiva (con fallback a promedio de p1-p4)
    const calcularPromedioNotas = (notasArray, getNota = (n) => n.nota_final) => {
        const validas = notasArray
            .map(n => {
                const val = getNota(n);
                return (val !== null && val !== undefined && val !== '' && parseFloat(val) > 0) ? parseFloat(val) : null;
            })
            .filter(v => v !== null && !isNaN(v));
        if (validas.length === 0) return null;
        const suma = validas.reduce((acc, v) => acc + v, 0);
        return Math.round(suma / validas.length);
    };

    // Promedio de notas pre-admisión (CPA/PAA)
    const calcularPromedioNotasPre = (notasArray) => {
        const valores = notasArray.map(n => {
            const rawVal = n.nota_prueba ?? n.nota ?? n.promedio;
            return rawVal !== null && rawVal !== undefined && rawVal !== '' ? parseFloat(rawVal) : null;
        }).filter(v => v !== null && !isNaN(v));
        if (valores.length === 0) return null;
        const suma = valores.reduce((acc, v) => acc + v, 0);
        return Math.round(suma / valores.length);
    };

    // Exportar a Excel
    const exportarExcel = () => {
        // Nota informativa al inicio
        const notaDisclaimer = [{
            'NRO': 'Nota: El siguiente boletín se presenta únicamente con fines informativos y no tiene validez curricular.'
        }];

        let data;

        if (historialType !== 'licenciatura') {
            // Caso Pre-admisión (CPA/PAA)
            data = notasPre.map((n, idx) => ({
                'NRO': idx + 1,
                'CÓDIGO': n.codigo || n.codigo_asignatura || '',
                'ASIGNATURA': n.asignatura,
                'NOTA': n.promedio || n.nota || n.nota_prueba || '0',
                'ESTADO': parseFloat(n.promedio || n.nota || n.nota_prueba) >= 61 ? 'APROBADO' : 'REPROBADO'
            }));
        } else if (periodoSeleccionado === 'final') {
            // Caso Licenciatura - Periodo Final
            data = notas.map((nota, idx) => {
                const notaFinalCalc = obtenerNotaFinalEfectiva(nota);
                const tieneSegundoTurno = nota.segunda_turno !== null && nota.segunda_turno !== undefined && nota.segunda_turno !== '' && parseFloat(nota.segunda_turno) > 0;
                const notaParaLiteral = tieneSegundoTurno ? parseFloat(nota.segunda_turno) : notaFinalCalc;
                return {
                    'NRO': idx + 1,
                    'ASIGNATURA': nota.asignatura,
                    '1°P': nota.p1 || '',
                    '2°P': nota.p2 || '',
                    '3°P': nota.p3 || '',
                    '4°P': nota.p4 || '',
                    'H.': nota.es_homologada ? 'SI' : 'NO',
                    'C.': nota.es_convalidacion ? 'SI' : 'NO',
                    'P.S.': nota.es_prueba_suficiencia ? 'SI' : 'NO',
                    'NOTA FINAL': notaFinalCalc !== null ? notaFinalCalc : '',
                    '2°T': nota.segunda_turno || '',
                    'NOTA LITERAL': notaParaLiteral !== null ? calcularNotaLiteral(notaParaLiteral) : '',
                    'ESTADO': calcularEstado(notaFinalCalc)
                };
            });
        } else {
            // Caso Licenciatura - Periodos 1-4
            data = notas.map((nota, idx) => ({
                'NRO': idx + 1,
                'SIGLA': nota.sigla || '',
                'ASIGNATURA': nota.asignatura,
                'PARALELO': nota.paralelo || '',
                'NIVEL': nota.nivel || '',
                'H.': nota.es_homologada ? 'SI' : 'NO',
                'C.': nota.es_convalidacion ? 'SI' : 'NO',
                'P.S.': nota.es_prueba_suficiencia ? 'SI' : 'NO',
                'PROMEDIO PROCESO 70%': nota.promedio_proceso || '',
                'PRÁCTICA 70PTS': nota.practica || '',
                'TEORÍA 30PTS': nota.teoria || '',
                'PROMEDIO EV. FINAL 30%': nota.promedio_eval_final || '',
                'NOTA FINAL': nota.nota_final || '',
                'ESTADO': calcularEstado(nota.nota_final)
            }));
        }

        // Obtener número de periodo para el header
        const periodoNum = periodoSeleccionado === 'primer' ? '1P' :
            periodoSeleccionado === 'segundo' ? '2P' :
                periodoSeleccionado === 'tercer' ? '3P' :
                    periodoSeleccionado === 'cuarto' ? '4P' : 'FINAL';

        // Obtener nombre de la hoja y archivo
        let sheetName = `Historial ${periodoSeleccionado}`;
        let fileName = `Historial_${periodoSeleccionado}_${anioSeleccionado}.xlsx`;

        if (historialType !== 'licenciatura') {
            sheetName = historialType === 'cpa' ? 'Historial CPA' : 'Historial PAA';
            fileName = `Historial_Admision_${historialType.toUpperCase()}.xlsx`;
        }

        const ws = XLSX.utils.json_to_sheet([...notaDisclaimer, {}, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, fileName);
    };

    if (loading) {
        return (
            <div className="p-6 flex justify-center items-center">
                <Loader className="animate-spin mr-2" /> Cargando historial académico...
            </div>
        );
    }

    const codigoEst = estudiante.codigo || 'S/C';
    const esPeriodoFinal = periodoSeleccionado === 'final';

    return (
        <div className="p-4 md:p-6">
            {/* Encabezado */}
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Estudiantes &gt; Historial Académico</h1>
            </div>

            {/* Barra de info y filtros */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4 justify-between">
                    {/* Info del estudiante */}
                    <div className="flex flex-wrap items-center gap-6 text-sm">
                        <div>
                            <span className="text-gray-500">Código:</span>
                            <span className="ml-2 font-semibold">{codigoEst}</span>
                        </div>
                        <div>
                            <span className="text-gray-500">Nombre y Apellidos:</span>
                            <span className="ml-2 font-semibold">
                                {estudiante.nombres} {estudiante.apellido_paterno} {estudiante.apellido_materno || ''}
                            </span>
                        </div>
                        <div>
                            <span className="text-gray-500">Carrera:</span>
                            <span className="ml-2 font-semibold">{estudiante.carrera || '-'}</span>
                        </div>
                        <div>
                            <span className="text-gray-500">Mención:</span>
                            <span className="ml-2 font-semibold">{estudiante.mencion || '-'}</span>
                        </div>
                    </div>

                    {/* Filtros y descarga */}
                    <div className="flex items-center gap-3">
                        {/* Selector de Tipo de Historial (Condicional) */}
                        <select
                            value={historialType}
                            onChange={(e) => setHistorialType(e.target.value)}
                            className="px-3 py-2 border border-blue-200 rounded-md text-sm font-semibold text-blue-800 bg-blue-50"
                        >
                            <option value="licenciatura">Carrera (Licenciatura)</option>
                            {(!estudiante.modalidad_postulacion ||
                                estudiante.modalidad_postulacion?.toUpperCase().includes('CPA') ||
                                estudiante.modalidad_postulacion?.toUpperCase().includes('CURSO')) && (
                                    <option value="cpa">Pre-admisión (CPA)</option>
                                )}
                            {(!estudiante.modalidad_postulacion ||
                                estudiante.modalidad_postulacion?.toUpperCase().includes('PAA') ||
                                estudiante.modalidad_postulacion?.toUpperCase().includes('PRUEBA')) && (
                                    <option value="paa">Prueba de Admisión (PAA)</option>
                                )}
                        </select>

                        {historialType === 'licenciatura' && (
                            <>
                                <select
                                    value={anioSeleccionado}
                                    onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
                                    className="px-3 py-2 border rounded-md text-sm"
                                >
                                    {aniosDisponibles.map(a => (
                                        <option key={a} value={a}>{a}</option>
                                    ))}
                                </select>

                                <select
                                    value={periodoSeleccionado}
                                    onChange={(e) => setPeriodoSeleccionado(e.target.value)}
                                    className="px-3 py-2 border rounded-md text-sm"
                                >
                                    {periodos.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </>
                        )}

                        <button
                            onClick={exportarExcel}
                            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700 transition"
                            title="Descargar Excel"
                        >
                            <Download size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabla de notas */}
            <div className="bg-white rounded-lg shadow overflow-x-auto">
                {historialType === 'licenciatura' ? (
                    esPeriodoFinal ? (
                        /* ========== TABLA PERIODO FINAL ========== */
                        <table className="w-full text-xs whitespace-nowrap">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-3 border text-center">No</th>
                                    <th className="px-3 py-3 border text-left">Asignatura</th>
                                    <th className="px-3 py-3 border text-center">1°P</th>
                                    <th className="px-3 py-3 border text-center">2°P</th>
                                    <th className="px-3 py-3 border text-center">3°P</th>
                                    <th className="px-3 py-3 border text-center">4°P</th>
                                    <th className="px-3 py-3 border text-center" title="Materia Homologada">H.</th>
                                    <th className="px-3 py-3 border text-center" title="Convalidación">C.</th>
                                    <th className="px-3 py-3 border text-center" title="Prueba de Suficiencia">P.S.</th>
                                    <th className="px-3 py-3 border text-center">Nota Final</th>
                                    <th className="px-3 py-3 border text-center">2°T</th>
                                    <th className="px-3 py-3 border text-center">Nota Literal</th>
                                    <th className="px-3 py-3 border text-center">Estado</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notas.length === 0 ? (
                                    <tr className="border-b">
                                        <td colSpan={13} className="px-3 py-8 text-center text-gray-500">No hay notas registradas</td>
                                    </tr>
                                ) : (
                                    <>
                                        {notas.map((nota, idx) => {
                                            const notaFinalCalc = obtenerNotaFinalEfectiva(nota);
                                            return (
                                                <tr key={nota.id_nota || idx} className="border-b hover:bg-gray-50">
                                                    <td className="px-3 py-3 border text-center">{idx + 1}</td>
                                                    <td className="px-3 py-3 border">{nota.asignatura}</td>
                                                    <td className="px-3 py-3 border text-center">{formatEntero(nota.p1)}</td>
                                                    <td className="px-3 py-3 border text-center">{formatEntero(nota.p2)}</td>
                                                    <td className="px-3 py-3 border text-center">{formatEntero(nota.p3)}</td>
                                                    <td className="px-3 py-3 border text-center">{formatEntero(nota.p4)}</td>
                                                    <td className="px-3 py-3 border text-center">{nota.es_homologada ? <Check size={16} className="text-green-600 mx-auto" /> : '-'}</td>
                                                    <td className="px-3 py-3 border text-center">{nota.es_convalidacion ? <Check size={16} className="text-blue-600 mx-auto" /> : '-'}</td>
                                                    <td className="px-3 py-3 border text-center">{nota.es_prueba_suficiencia ? <Check size={16} className="text-purple-600 mx-auto" /> : '-'}</td>
                                                    <td className="px-3 py-3 border text-center font-bold">{formatEntero(notaFinalCalc)}</td>
                                                    <td className="px-3 py-3 border text-center">{nota.segunda_turno ?? '-'}</td>
                                                    <td className="px-3 py-3 border text-center font-medium">
                                                        {(() => {
                                                            const tieneSegundoTurno = nota.segunda_turno !== null && nota.segunda_turno !== undefined && nota.segunda_turno !== '' && parseFloat(nota.segunda_turno) > 0;
                                                            const notaParaLiteral = tieneSegundoTurno ? parseFloat(nota.segunda_turno) : notaFinalCalc;
                                                            return notaParaLiteral !== null ? calcularNotaLiteral(notaParaLiteral) : '-';
                                                        })()}
                                                    </td>
                                                    <td className="px-3 py-3 border text-center">
                                                        <span className={`text-xs font-medium ${calcularEstado(notaFinalCalc) === 'APROBADO' ? 'text-green-600' : calcularEstado(notaFinalCalc) === 'REPROBADO' ? 'text-red-600' : 'text-gray-500'}`}>
                                                            {calcularEstado(notaFinalCalc)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        {/* ── FILA PROMEDIO FINAL (Periodo Final) ── */}
                                        {(() => {
                                            const promedio = calcularPromedioNotas(notas, obtenerNotaFinalEfectiva);
                                            if (promedio === null) return null;
                                            const estado = calcularEstado(promedio);
                                            return (
                                                <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                                                    <td colSpan={9} className="px-3 py-3 border text-right text-blue-800 text-xs">
                                                        PROMEDIO FINAL
                                                    </td>
                                                    <td className="px-3 py-3 border text-center text-blue-800">
                                                        {promedio}
                                                    </td>
                                                    <td className="px-3 py-3 border text-center text-gray-400">—</td>
                                                    <td className="px-3 py-3 border text-center text-blue-800">
                                                        {calcularNotaLiteral(promedio)}
                                                    </td>
                                                    <td className="px-3 py-3 border text-center">
                                                        <span className={`text-xs font-bold ${estado === 'APROBADO' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {estado}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                    </>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        /* ========== TABLA PERIODOS 1-4 ========== */
                        <table className="w-full text-xs border-collapse">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-2 py-2 border text-center" rowSpan={2}>No</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2}>Sigla</th>
                                    <th className="px-2 py-2 border text-left" rowSpan={2}>Asignatura</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2}>Paralelo</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2}>Nivel</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2}>Promedio de<br />Proceso 70%</th>
                                    <th className="px-2 py-2 border text-center" colSpan={3}>Evaluación Final</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2}>Nota<br />Final {periodoSeleccionado === 'primer' ? '1P' : periodoSeleccionado === 'segundo' ? '2P' : periodoSeleccionado === 'tercer' ? '3P' : '4P'}</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2} title="Materia Homologada">H.</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2} title="Convalidación">C.</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2} title="Prueba de Suficiencia">P.S.</th>
                                    <th className="px-2 py-2 border text-center" rowSpan={2}>Estado</th>
                                </tr>
                                <tr>
                                    <th className="px-2 py-2 border text-center bg-gray-100">Práctica<br />70PTS</th>
                                    <th className="px-2 py-2 border text-center bg-gray-100">Teoría<br />30PTS</th>
                                    <th className="px-2 py-2 border text-center bg-gray-100">Promedio Ev.<br />Final 30%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notas.length === 0 ? (
                                    <tr className="border-b">
                                        <td colSpan={13} className="px-2 py-8 text-center text-gray-500">No hay materias en este periodo</td>
                                    </tr>
                                ) : (
                                    <>
                                        {notas.map((nota, idx) => (
                                            <tr key={nota.id_nota || idx} className="border-b hover:bg-gray-50">
                                                <td className="px-2 py-2 border text-center">{idx + 1}</td>
                                                <td className="px-2 py-2 border text-center">{nota.sigla || ''}</td>
                                                <td className="px-2 py-2 border">{nota.asignatura}</td>
                                                <td className="px-2 py-2 border text-center">{nota.paralelo || '-'}</td>
                                                <td className="px-2 py-2 border text-center">{nota.nivel || '-'}</td>
                                                <td className="px-2 py-2 border text-center font-medium bg-gray-50">
                                                    {formatEntero(nota.promedio_proceso)}
                                                </td>
                                                <td className="px-2 py-2 border text-center bg-yellow-50/40">
                                                    {formatEntero(nota.practica)}
                                                </td>
                                                <td className="px-2 py-2 border text-center bg-yellow-50/40">
                                                    {formatEntero(nota.teoria)}
                                                </td>
                                                <td className="px-2 py-2 border text-center bg-yellow-50/40">
                                                    {formatEntero(nota.promedio_eval_final)}
                                                </td>
                                                <td className="px-2 py-2 border text-center font-bold bg-yellow-100">
                                                    {formatEntero(nota.nota_final)}
                                                </td>
                                                <td className="px-2 py-2 border text-center">{nota.es_homologada ? <Check size={14} className="text-green-600 mx-auto" strokeWidth={3} /> : '-'}</td>
                                                <td className="px-2 py-2 border text-center">{nota.es_convalidacion ? <Check size={14} className="text-blue-600 mx-auto" strokeWidth={3} /> : '-'}</td>
                                                <td className="px-2 py-2 border text-center">{nota.es_prueba_suficiencia ? <Check size={14} className="text-purple-600 mx-auto" strokeWidth={3} /> : '-'}</td>
                                                <td className="px-2 py-2 text-center">
                                                    <span className={`text-xs font-medium ${calcularEstado(nota.nota_final) === 'APROBADO' ? 'text-green-600' : calcularEstado(nota.nota_final) === 'REPROBADO' ? 'text-red-600' : 'text-gray-500'}`}>
                                                        {calcularEstado(nota.nota_final)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}

                                        {/* ── FILA PROMEDIO FINAL (Periodos 1-4) ── */}
                                        {(() => {
                                            const promedio = calcularPromedioNotas(notas);
                                            if (promedio === null) return null;
                                            const estado = calcularEstado(promedio);
                                            return (
                                                <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                                                    <td colSpan={9} className="px-2 py-2 border text-right text-blue-800 text-xs">
                                                        PROMEDIO FINAL
                                                    </td>
                                                    <td className="px-2 py-2 border text-center bg-yellow-100 text-blue-800">
                                                        {promedio}
                                                    </td>
                                                    <td colSpan={3} className="px-2 py-2 border text-center text-gray-400">—</td>
                                                    <td className="px-2 py-2 text-center">
                                                        <span className={`text-xs font-bold ${estado === 'APROBADO' ? 'text-green-600' : 'text-red-600'}`}>
                                                            {estado}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                    </>
                                )}
                            </tbody>
                        </table>
                    )
                ) : (
                    /* ========== VISTA PRE-ADMISIÓN (CPA/PAA) ========== */
                    <table className="w-full text-sm">
                        <thead className="bg-[#1e3a8a] text-white">
                            <tr>
                                <th className="px-4 py-3 text-center font-semibold border">No</th>
                                <th className="px-4 py-3 text-center font-semibold border">Código</th>
                                <th className="px-4 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-4 py-3 text-center font-semibold border">Nota</th>
                                <th className="px-4 py-3 text-center font-semibold border">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {notasPre.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">No se encontraron notas registradas en esta etapa previa.</td>
                                </tr>
                            ) : (
                                <>
                                    {notasPre.map((n, idx) => {
                                        const notaFinalPre = parseFloat(n.nota_prueba) > 0 ? n.nota_prueba :
                                            parseFloat(n.nota) > 0 ? n.nota :
                                                parseFloat(n.promedio) > 0 ? n.promedio :
                                                    (n.nota_prueba || n.nota || n.promedio || '0.00');
                                        const valorNumerico = parseFloat(notaFinalPre);

                                        return (
                                            <tr key={n.id_nota || idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-3 text-center border">{idx + 1}</td>
                                                <td className="px-4 py-3 text-center border font-mono text-blue-700">{n.codigo || n.codigo_asignatura}</td>
                                                <td className="px-4 py-3 border">{n.asignatura}</td>
                                                <td className="px-4 py-3 text-center border font-semibold">
                                                    {formatEntero(notaFinalPre)}
                                                </td>
                                                <td className="px-4 py-3 text-center border">
                                                    <span className={`text-xs font-bold ${valorNumerico >= 61 ? 'text-green-600' : 'text-red-600'}`}>
                                                        {valorNumerico >= 61 ? 'APROBADO' : 'REPROBADO'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {/* ── FILA PROMEDIO FINAL (CPA/PAA) ── */}
                                    {(() => {
                                        const promedio = calcularPromedioNotasPre(notasPre);
                                        if (promedio === null) return null;
                                        const estado = promedio >= 61 ? 'APROBADO' : 'REPROBADO';
                                        return (
                                            <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                                                <td colSpan={3} className="px-4 py-3 border text-right text-blue-800 text-sm">
                                                    PROMEDIO FINAL
                                                </td>
                                                <td className="px-4 py-3 border text-center text-blue-800">
                                                    {promedio}
                                                </td>
                                                <td className="px-4 py-3 border text-center">
                                                    <span className={`text-xs font-bold ${estado === 'APROBADO' ? 'text-green-600' : 'text-red-600'}`}>
                                                        {estado}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })()}
                                </>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default HistorialAcademicoEstudiante;